import {
    makeItemsListUrl,
    trans,
} from '@frontend/helpers'
import type {AdCategory} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useItemsSearchCategorySelectedHandler} from '@frontend/modules/public/ads/components/search/hooks/useItemsSearchCategorySelectedHandler'
import {useAdsStore} from '@frontend/stores'
import {
    mdiChevronDown,
    mdiChevronRight,
} from '@mdi/js'
import {iconMenuBurgerAlt} from '@shared/helpers/icons'
import clsx from 'clsx'
import {
    useRef,
    useState,
} from 'react'
import {Link} from 'react-router-dom'
import {FadeSwitch} from 'swayok-react-mdb-ui-kit/components/Animation/FadeSwitch'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {Modal} from 'swayok-react-mdb-ui-kit/components/Modal/Modal'
import {ModalBody} from 'swayok-react-mdb-ui-kit/components/Modal/ModalBody'
import {ModalContent} from 'swayok-react-mdb-ui-kit/components/Modal/ModalContent'
import {ModalHeaderCloseButton} from 'swayok-react-mdb-ui-kit/components/Modal/ModalHeaderCloseButton'

interface CategorySelectorModalProps {
    iconMode?: boolean
}

// Компонент для выбора категории объявлений.
export function CategorySelectorModal(props: CategorySelectorModalProps) {

    const {
        iconMode,
    } = props

    const categories = useAdsStore(
        state => state.categories
    )

    const category = useAdsStore(
        state => state.searchPanelCategory
    )

    const onCategoryChange = useItemsSearchCategorySelectedHandler()

    const [
        hoveredCategory,
        setHoveredCategory,
    ] = useState<AdCategory | null>(categories[0] || null)

    // Видимость модального окна.
    const [
        isModalVisible,
        setIsModalVisible,
    ] = useState<boolean>(false)

    const isTouchEventRef = useRef<boolean>(false)

    // Скрыть модальное окно.
    const hideModal = () => setIsModalVisible(false)

    // Отрисовка категорий верхнего уровня.
    const renderTopLevelCategories = () => {
        const buttonClassName: string = 'top-level-category py-2 ps-3 pe-2 fs-6 full-width flex-row-between'
        const ret = [
            // Опция: "Все категории"
            <li key="category-all">
                <Button
                    color="light"
                    className={buttonClassName}
                    onClick={e => {
                        e.preventDefault()
                        onCategoryChange(null)
                        hideModal()
                    }}
                    onMouseOver={() => setHoveredCategory(null)}
                >
                    <span>{trans().common.ads_filter.all_ads}</span>
                </Button>
            </li>,
        ]
        for (const category of categories) {
            ret.push(
                <li key={'category-' + String(category.id)}>
                    <Button
                        color="light"
                        className={clsx(
                            buttonClassName,
                            hoveredCategory?.id === category.id ? 'active' : null
                        )}
                        onClick={e => {
                            e.preventDefault()
                            onCategoryChange(category)
                            hideModal()
                        }}
                        onMouseOver={() => !isTouchEventRef.current && setHoveredCategory(category)}
                        onTouchStart={() => isTouchEventRef.current = true}
                        onTouchEnd={e => {
                            isTouchEventRef.current = false
                            if (e.cancelable) {
                                e.preventDefault()
                                if (hoveredCategory?.id === category.id) {
                                    // Повторное нажатие: переходим к категории.
                                    onCategoryChange(category)
                                    hideModal()
                                } else {
                                    setHoveredCategory(category)
                                }
                            }
                        }}
                    >
                        <span>{category.name}</span>
                        <Icon
                            path={mdiChevronRight}
                            reuse="mdiChevronRight"
                            className="ms-2"
                        />
                    </Button>
                </li>
            )
        }
        return (
            <ul className="list-unstyled">
                {ret}
            </ul>
        )
    }

    const renderSubCategories = () => {
        if (!hoveredCategory) {
            return <div key="no-sub-categories" />
        }
        const ret = []
        for (const subCategory of hoveredCategory.children) {
            ret.push(
                <div
                    key={subCategory.id}
                    className="sub-category pb-4"
                >
                    <div className="mb-1">
                        <Link
                            to={makeItemsListUrl(subCategory)}
                            className="fw-700"
                            onClick={e => {
                                e.preventDefault()
                                onCategoryChange(subCategory)
                                hideModal()
                            }}
                        >
                            {subCategory.name}&nbsp;›
                        </Link>
                    </div>
                    {subCategory.children?.map(subCategory => (
                        <div
                            key={subCategory.id}
                            className="mb-1"
                        >
                            <Link
                                to={makeItemsListUrl(subCategory)}
                                className="d-inline-block lh-sm"
                                onClick={e => {
                                    e.preventDefault()
                                    onCategoryChange(subCategory)
                                    hideModal()
                                }}
                            >
                                {subCategory.name}
                            </Link>
                        </div>
                    ))}
                </div>
            )
        }
        return (
            <div
                key={hoveredCategory?.id}
                className="full-height overflow-hidden flex-container-column"
            >
                <div className="mb-4">
                    <Link
                        to={makeItemsListUrl(hoveredCategory)}
                        className="with-icon-flex fs-4 fw-600"
                        onClick={e => {
                            e.preventDefault()
                            onCategoryChange(hoveredCategory)
                            hideModal()
                        }}
                    >
                        {hoveredCategory.name}&nbsp;›
                    </Link>
                </div>
                <div className="sub-categories-menu pb-4 pe-4">
                    {ret}
                </div>
            </div>
        )
    }

    return (
        <div className={iconMode ? 'offered-items-filter-categories-icon' : ''}>
            {iconMode ? (
                <Button
                    color="link"
                    className="with-icon-flex btn-offered-items-filter-category-selector"
                    onClick={() => setIsModalVisible(true)}
                >
                    <Icon
                        path={iconMenuBurgerAlt}
                        color="primary"
                    />
                </Button>
            ) : (
                <Button
                    color="gray"
                    className="btn-offered-items-filter-category-selector"
                    outline
                    hasIcon
                    onClick={() => setIsModalVisible(true)}
                >
                    <Icon
                        path={iconMenuBurgerAlt}
                        className="me-0 offered-items-filter-button-icon"
                    />
                    <span className="flex-1 ms-1 me-1 text-center">
                        {category ? category.name : trans().common.ads_filter.any_category}
                    </span>
                    <Icon
                        path={mdiChevronDown}
                        className="offered-items-filter-button-chevron"
                    />
                </Button>
            )}

            <Modal
                show={isModalVisible}
                size="xl"
                id="offered-items-filter-categories-menu-modal"
                onClose={hideModal}
                onShow={() => {
                    if (!hoveredCategory) {
                        setHoveredCategory(categories[0] || null)
                    }
                }}
            >
                <ModalContent>
                    <ModalHeaderCloseButton
                        onClose={hideModal}
                        className="btn-close-modal"
                        floating
                    />
                    <ModalBody className="p-0 pt-4 d-flex flex-row align-items-stretch justify-content-start">
                        <div className="top-level-categories-menu pb-3 px-2">
                            {renderTopLevelCategories()}
                        </div>
                        <div className="sub-categories-menu-container ms-4 flex-1">
                            <FadeSwitch transitionKey={hoveredCategory?.id ?? 'none'}>
                                {renderSubCategories()}
                            </FadeSwitch>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    )
}
